import { AgentSearchService } from 'ag-common-svc/public-api';
import { Injectable } from '@angular/core';
import { ConferenceService } from 'ag-common-svc/lib/services/conference.service';
import { format, formatDistanceToNow, isValid, toDate } from 'date-fns';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  AGENCY_TYPE,
  AgencyKeys,
  Agent,
  AgentKeys,
  ChangeSourceType,
  ConferenceKeys,
  EmailAddressKeys,
  Entity,
  EntityPermissionActivityKeys,
  InviteeStatus,
  Registrant,
  RegistrantKeys,
  RegistrantModelKeys,
} from 'ag-common-lib/public-api';
import { AgencyService, AuthService, ConferenceRegistrantsService } from 'ag-common-svc/public-api';
import DataSource from 'devextreme/data/data_source';
import ArrayStore from 'devextreme/data/array_store';
import { AttendeeDetailsModalSection } from '../conferences/modals/attendee-details-modal/attendee-details-modal.model';
import { Attendee } from 'ag-common-svc/lib/utils/attendees';
import { HasPermissionPipe } from 'ag-common-svc/shared/pipes/has-permission.pipe';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import { EventInquiryRequestsService } from 'ag-common-svc/lib/services/event-inquire-requests.service';

@Injectable()
export class ConferenceRegistrationGridService {
  private _selectedAgentDbId$ = new BehaviorSubject(null);
  agentDetailsModalVisible$: Observable<boolean> = this._selectedAgentDbId$.pipe(map(agentDbId => !!agentDbId));
  selectedAgent$: Observable<Agent> = this._selectedAgentDbId$.pipe(
    filter(Boolean),
    mergeMap(agentDbId => {
      return from(this.agentSearchService.getById(agentDbId));
    }),
    shareReplay(1),
  );

  private _showAttendeeDetailsModal$ = new Subject<{ attendee: Attendee; section: AttendeeDetailsModalSection }>();
  showAttendeeDetailsModal$ = this._showAttendeeDetailsModal$.asObservable();

  private _refreshTrigger$ = new Subject<void>();

  private _lastUpdateDate$ = new BehaviorSubject<Date>(null);
  // switchMap, not mergeMap: _lastUpdateDate$ is a BehaviorSubject that never completes, so a
  // mergeMap left one live inner subscription behind on every tick — one every 30s, for as long as
  // the page stayed open, each re-running formatDistanceToNow on every update.
  lastUpdateDate$ = timer(1000, 1000 * 30).pipe(
    switchMap(() => this._lastUpdateDate$),
    map(date => {
      if (!date) {
        return 'Last Update: Now';
      }

      return `Last Update: ${formatDistanceToNow(date)} ago`;
    }),
  );

  private _conferenceDbId$ = new BehaviorSubject<string>(null);
  conferenceDbId$ = this._conferenceDbId$.asObservable();
  conference$ = this._conferenceDbId$.pipe(
    filter(Boolean),
    // switchMap so switching conference tears down the previous conference's document listener
    // instead of leaving it live alongside the new one.
    switchMap(conferenceDbId => {
      return this.conferenceService.getDocumentData(conferenceDbId);
    }),
    shareReplay(1),
  );
  embeddedFormEnabled$ = this.conference$.pipe(map(conference => conference?.[ConferenceKeys.embeddedFormEnabled]));

  inProgress$ = new BehaviorSubject<boolean>(false);

  conferencesList$ = combineLatest({
    conferences: this.conferenceService.getList([], { sortField: 'start_date' }).pipe(startWith([])),
    loggedInAgent: this.authService.loggedInAgent$,
    hasEventListPermission: this.hasPermissionPipe.transform(Entity.conferenceList, EntityPermissionActivityKeys.read),
    hasEventLisAdmintPermission: this.hasPermissionPipe.transform(
      Entity.conferenceListAdmin,
      EntityPermissionActivityKeys.read,
    ),
  }).pipe(
    map(({ conferences, loggedInAgent, hasEventListPermission, hasEventLisAdmintPermission }) => {
      if (hasEventListPermission && !hasEventLisAdmintPermission) {
        // show only conferences that user has access to eventList permission only
        const agentDbId = loggedInAgent?.[BaseModelKeys.dbId];
        return conferences?.filter(conference => {
          const createdByAgentDbId = [conference?.[BaseModelKeys.createdByAgentDbId]];
          const permissionConfigurationIds = conference?.[ConferenceKeys.permissionConfigurationIds] ?? [];
          const permissionIds = new Set(createdByAgentDbId.concat(permissionConfigurationIds));
          return permissionIds?.has(agentDbId);
        });
      }
      return conferences;
    }),
    map(conferences => {
      return conferences?.map(conference => {
        const conferenceDbId = conference?.dbId;
        const conferenceName = conference?.event_name;
        const conferenceStartDate = isValid(toDate(conference?.[ConferenceKeys.startDate]))
          ? format(conference?.start_date as Date, "'from' MM-dd-yyyy")
          : null;
        const conferenceEndDate = isValid(toDate(conference?.[ConferenceKeys.endDate]))
          ? format(conference?.end_date as Date, "'to' MM-dd-yyyy")
          : null;

        const conferenceDuration = [conferenceStartDate, conferenceEndDate].filter(Boolean).join(' ');
        const description = `${conferenceName} (${conferenceDuration})`;

        return { conferenceDbId, description };
      });
    }),
    shareReplay(1),
  );

  agencies$ = this.agencyService.getList().pipe(shareReplay(1));
  mgaList$ = this.agencies$.pipe(
    map(agencies => agencies?.filter(agency => agency?.[AgencyKeys.agencyType] === AGENCY_TYPE.MGA)),
  );

  // getRegistrantsByConferenceId is a live Firestore listener. It used to be cut down to a single
  // read with take(1), which froze every status, owner and registration-state column at the moment
  // the conference was picked: edits made in the attendee details modal (which write through
  // ConferenceRegistrantsService directly, not through this service) and edits made by other users
  // were never reflected, so the grid showed statuses that were simply wrong.
  registrants$: Observable<Registrant[]> = combineLatest({
    conferenceDbId: this._conferenceDbId$,
    _refreshTrigger$: this._refreshTrigger$.pipe(startWith(null)),
  }).pipe(
    map(({ conferenceDbId }) => conferenceDbId),
    filter(Boolean),
    switchMap(conferenceDbId => {
      // ignoreCachedSnapshots: Firestore runs with persistentLocalCache, so a newly attached
      // listener emits this browser's IndexedDB contents for the query first. That set can be a
      // subset of the collection — in the worst case only the registrants touched most recently,
      // e.g. the single one just opened in the details modal — and rendering it looks exactly like
      // the conference lost every other participant. Server-confirmed snapshots only.
      return this.conferenceRegistrantsService.getRegistrantsByConferenceId(conferenceDbId, 'last_name', [], true);
    }),
    // Stamped only once data actually arrives — it used to be stamped on the trigger as well, so
    // "Last Update: a minute ago" described the request, not the rows on screen.
    tap(() => {
      this._lastUpdateDate$.next(new Date());
    }),
    shareReplay(1),
  );

  participationRequests$ = this._conferenceDbId$.pipe(
    mergeMap((eventDbId: string) => {
      return this.eventInquiryRequestsService.getList(eventDbId);
    }),
  );

  participatedAttendees$ = combineLatest({ conference: this.conference$, registrants: this.registrants$ }).pipe(
    map(({ registrants, conference }) =>
      registrants
        ?.filter(registrant => {
          const registrantData = registrant?.[RegistrantModelKeys.data];
          const inviteeStatus = registrantData?.[RegistrantKeys.inviteeStatus];
          const inviteeOutcomeStatus = registrantData?.[RegistrantKeys.inviteeOutcomeStatus];

          return (
            inviteeStatus !== InviteeStatus.declined &&
            !new Set([InviteeStatus.cancelled, InviteeStatus.doNotShowUp]).has(inviteeOutcomeStatus)
          );
        })
        .map(registrant => new Attendee(conference, registrant)),
    ),
    // Emits the plain array. This used to wrap every emission in a brand new DataSource/ArrayStore,
    // which DevExtreme treats as an entirely different source and re-renders from scratch. Now that
    // registrants$ is live again that would happen on every write anywhere in the conference, so the
    // grid binds the array directly and diffs by key via repaintChangesOnly.
    shareReplay(1),
  );

  declinedRegistrants$ = this.registrants$.pipe(
    map(registrants =>
      registrants?.filter(registrant => {
        const registrantData = registrant?.[RegistrantModelKeys.data];
        const inviteeStatus = registrantData?.[RegistrantKeys.inviteeStatus];
        const inviteeOutcomeStatus = registrantData?.[RegistrantKeys.inviteeOutcomeStatus];

        return (
          inviteeStatus === InviteeStatus.declined ||
          new Set([InviteeStatus.cancelled, InviteeStatus.doNotShowUp]).has(inviteeOutcomeStatus)
        );
      }),
    ),
  );

  assignOwnerList$ = this.conference$.pipe(
    map(conference => conference?.[ConferenceKeys.permissionConfigurationIds]),
    mergeMap(permissionConfigurationIds => {
      return from(this.agentSearchService.getByIds(permissionConfigurationIds));
    }),
    map(assignOwnerList => {
      assignOwnerList = assignOwnerList.map(item => ({
        ...item,
        [AgentKeys.email_addresses]: item[AgentKeys.email_addresses] || [
          {
            [EmailAddressKeys.address]: item[AgentKeys.p_email],
            [EmailAddressKeys.isLogin]: true,
          },
        ],
      }));
      return new DataSource({
        store: new ArrayStore({
          key: 'dbId',
          data: Array.isArray(assignOwnerList) ? assignOwnerList : [],
        }),
      });
    }),
    shareReplay(1),
  );

  constructor(
    private agencyService: AgencyService,
    private agentSearchService: AgentSearchService,
    private conferenceService: ConferenceService,
    private conferenceRegistrantsService: ConferenceRegistrantsService,
    private toastrService: ToastrService,
    private authService: AuthService,
    private hasPermissionPipe: HasPermissionPipe,
    private eventInquiryRequestsService: EventInquiryRequestsService,
  ) {
    this.lastUpdateDate$.subscribe();
  }

  setConferenceDbId = conferenceDbId => {
    this._conferenceDbId$.next(conferenceDbId);
  };

  showAgentDetails = agentDbiId => {
    this._selectedAgentDbId$.next(agentDbiId);
  };

  onAgentModalHidden = () => {
    this._selectedAgentDbId$.next(null);
  };

  forceRefresh = () => {
    this._refreshTrigger$.next();
  };

  showAttendeeModal = (attendee: Attendee, section = AttendeeDetailsModalSection.generalInfo) => {
    this._showAttendeeDetailsModal$.next({
      attendee,
      section,
    });
  };

  updateRegistrant(conferenceId: string, registrantId: string, update: Partial<Registrant>): Promise<boolean> {
    this.inProgress$.next(true);
    return this.conferenceRegistrantsService
      .update(conferenceId, registrantId, update, ChangeSourceType.regularArm)
      .then(() => {
        this.forceRefresh();
        return true;
      })
      .catch(err => {
        this.toastrService.error('Registrant was Not Updated');
        throw err;
      })
      .finally(() => {
        this.inProgress$.next(false);
      });
  }
}
